import { useEffect, useState } from 'react';

import { Calendar, Inbox, Lock } from '../icons.js';

import { describeEmailLoginFailure, describePasskeyFailure } from './errors.js';

import type { Identity, Session } from '../../core/auth/session.js';

/**
 * Вход и регистрация на одном экране: почта, код из письма и passkey.
 *
 * Отдельной регистрации нет намеренно: код на почту и так доказывает владение
 * адресом, а вторая форма заставила бы человека сначала вспомнить, заводил ли
 * он уже учётную запись (этап 20 плана). Поэтому первый шаг так и назван —
 * «Войти или создать аккаунт».
 *
 * Слева — что это за приложение. Прежний экран состоял из одного слова и поля,
 * и пришедший по ссылке не узнавал, куда попал, пока не войдёт. На телефоне от
 * описания остаётся шапка: список возможностей выталкивал форму за первый экран.
 *
 * Код нужен рядом со ссылкой не для полноты: письмо часто приходит на телефон,
 * а войти надо на ноутбуке — ссылка в этом случае открывается не там, где
 * нужно (раздел 6 архитектуры).
 */

export interface LoginScreenProps {
  session: Session;
  onSignedIn: (identity: Identity) => void;
  /** Начальный адрес: `GET /auth/callback` возвращает сюда с `?login=code&email=…`. */
  initialEmail?: string;
  /** Письмо уже ушло — показываем сразу поле кода. */
  initialStage?: 'email' | 'code';
}

/**
 * Пауза перед повторным письмом. Сервер пускает три письма за четверть часа, и
 * без паузы их сжигают тремя нажатиями подряд, пока первое ещё в пути.
 */
const RESEND_AFTER_MS = 60_000;
const CODE_LENGTH = 6;

export function LoginScreen(props: LoginScreenProps): React.ReactElement {
  const [stage, setStage] = useState<'email' | 'code'>(props.initialStage ?? 'email');
  const [email, setEmail] = useState(props.initialEmail ?? '');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  // Пришедшему из письма в чужом браузере ждать незачем: письмо у него уже есть,
  // и если код не подошёл, новое нужно сразу.
  const [resendAt, setResendAt] = useState(0);
  const [now, setNow] = useState(() => Date.now());

  const waiting = resendAt > now;

  useEffect(() => {
    if (!waiting) return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [waiting]);

  async function guard(
    action: () => Promise<void>,
    describe: (failure: unknown) => string = describeEmailLoginFailure,
  ): Promise<void> {
    setBusy(true);
    setError(undefined);
    try {
      await action();
    } catch (failure) {
      setError(describe(failure));
    } finally {
      setBusy(false);
    }
  }

  async function sendEmail(): Promise<void> {
    await props.session.requestEmailLink(email);
    const sent = Date.now();
    setNow(sent);
    setResendAt(sent + RESEND_AFTER_MS);
    setCode('');
    setStage('code');
  }

  function submitCode(value: string): void {
    void guard(async () => {
      try {
        props.onSignedIn(await props.session.signInWithCode(email, value));
      } catch (failure) {
        // Неподошедший код стирается: найти ошибку в шести цифрах без
        // разделителей дольше, чем набрать их заново.
        setCode('');
        throw failure;
      }
    });
  }

  const left = Math.max(0, Math.ceil((resendAt - now) / 1000));
  const clock = `${String(Math.floor(left / 60))}:${String(left % 60).padStart(2, '0')}`;

  return (
    <main className="login">
      <section className="login__intro">
        <p className="login__brand">
          <img src="/icon.svg" alt="" width="28" height="28" />
          Onlap
        </p>
        <h1 className="login__title">Что делаем на этой неделе</h1>
        <p className="login__lede">
          Личный трекер по неделям. На телефоне и ноутбуке, даже без интернета.
        </p>
        <ul className="login__features">
          <li>
            <Calendar />
            Каждую неделю — новый спринт
          </li>
          <li>
            <Inbox />
            Задачи на потом — во Входящих
          </li>
          <li>
            <Lock />
            Пароль не нужен
          </li>
        </ul>
      </section>

      <section className="login__card" aria-label="Вход">
        {stage === 'email' ? (
          <>
            <form
              className="login__form"
              onSubmit={(event) => {
                event.preventDefault();
                void guard(sendEmail);
              }}
            >
              <p className="login__step">Шаг 1 из 2</p>
              <h2 className="login__heading">Войти или создать аккаунт</h2>
              <p className="login__hint">
                Пришлём код на почту — он подходит и для входа, и для регистрации.
              </p>
              <label htmlFor="login-email">Почта</label>
              <input
                id="login-email"
                type="email"
                autoComplete="username webauthn"
                placeholder="you@example.com"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
              <button type="submit" className="login__primary" disabled={busy}>
                {busy ? 'Отправляем…' : 'Получить код'}
              </button>
            </form>

            <p className="login__divider">или</p>

            {/*
              «Passkey» — слово, которое знают те, кому оно уже не нужно
              объяснять. Остальные узнают ключ по тому, как он спрашивается:
              отпечатком или лицом.
            */}
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                void guard(async () => {
                  props.onSignedIn(await props.session.signInWithPasskey());
                }, describePasskeyFailure);
              }}
            >
              Войти по отпечатку или лицу
            </button>
            <p className="login__small">
              Сработает, если ключ входа уже привязан на этом устройстве.
            </p>
          </>
        ) : (
          <form
            className="login__form"
            onSubmit={(event) => {
              event.preventDefault();
              submitCode(code);
            }}
          >
            <p className="login__step">Шаг 2 из 2</p>
            <h2 className="login__heading">Проверьте почту</h2>
            <p className="login__hint">
              {props.initialStage === 'code' && resendAt === 0
                ? `Ссылка открылась не в том браузере, где запрашивали вход. Введите здесь код из того же письма${email === '' ? '' : ` — оно ушло на ${email}`}.`
                : `Код и ссылка ушли на ${email}. Код удобен, если письмо открылось на другом устройстве.`}
            </p>
            <label htmlFor="login-code">Код из письма</label>
            <input
              id="login-code"
              className="login__code"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="\d{6}"
              maxLength={CODE_LENGTH}
              required
              autoFocus
              value={code}
              onChange={(event) => {
                // Код вставляют из письма как попало — с пробелами, дефисом,
                // хвостом строки. Цифр шесть, и шестая сама отправляет форму:
                // нажимать «Войти» после набора — лишний шаг на телефоне.
                const digits = event.target.value.replace(/\D/g, '').slice(0, CODE_LENGTH);
                setCode(digits);
                if (digits.length === CODE_LENGTH && !busy) submitCode(digits);
              }}
            />
            <button type="submit" className="login__primary" disabled={busy}>
              {busy ? 'Проверяем…' : 'Войти'}
            </button>
            <div className="login__more">
              <p>
                Не пришло? Загляните в «Спам»: первое письмо с нового адреса часто попадает туда.
              </p>
              {waiting ? (
                <span>Отправить снова можно через {clock}</span>
              ) : (
                <button
                  type="button"
                  className="login__link"
                  disabled={busy}
                  onClick={() => {
                    void guard(sendEmail);
                  }}
                >
                  Отправить снова
                </button>
              )}
              <button
                type="button"
                className="login__link"
                onClick={() => {
                  setStage('email');
                  setCode('');
                  setError(undefined);
                }}
              >
                Другой адрес
              </button>
            </div>
          </form>
        )}

        {error !== undefined && (
          <p className="login__error" role="alert">
            {error}
          </p>
        )}
      </section>
    </main>
  );
}
